import {
  injectRequiredWithSelf,
  checkValuesAlreadyProvided,
  CreateInjectionKeysRecord,
  type InjectionKeysRecord,
  type InjectionValuesRecord
} from "@mcwd/util-vue";
import { provide } from "vue";
import { AnyFormSettingsKey } from '../form-settings/form-experience/form-settings-keys.js';
import {
  getFormSettings,
  getFormClasses,
  type IFormSettings,
  FormExperienceSettingsObject,
} from "../form-settings/form-settings-factory.js"; 
import type { MarketoFormSettingsKey, MarketoFormSettingsObject } from '../form-settings/marketo-form/marketo-form-setting.js';

import { useFormLogger } from "./use-form-logger.js";


type UseFormSettingsInjectionValues = InjectionValuesRecord<{
  FormSettings: Readonly<IFormSettings>;
  FormClasses: Readonly<ReturnType<typeof getFormClasses>>;
}>;

type UseFormSettingsInjectionKeys = InjectionKeysRecord<UseFormSettingsInjectionValues>;

const UseFormSettingsInjectionKeys = CreateInjectionKeysRecord<UseFormSettingsInjectionKeys>({
  FormSettings: "FormSettings",
  FormClasses: "FormClasses",
});

export function initFormSettings(formSettingsKey: AnyFormSettingsKey) {
  if (checkValuesAlreadyProvided(UseFormSettingsInjectionKeys)) {
    return;
  }
  const FormSettings = getFormSettings<IFormSettings>(formSettingsKey);
  const FormClasses = getFormClasses(formSettingsKey);

  provide(UseFormSettingsInjectionKeys.FormSettings, FormSettings);
  provide(UseFormSettingsInjectionKeys.FormClasses, FormClasses);
}

type SettingsKeyType<TFormOrFormExperience extends "MarketoForm" | "FormExperience", TFormSettingsKey extends MarketoFormSettingsKey | AnyFormSettingsKey>
  = TFormOrFormExperience extends "MarketoForm"
    ? Extract<TFormSettingsKey, MarketoFormSettingsKey>
    : TFormOrFormExperience extends "FormExperience"
    ? Extract<TFormSettingsKey, AnyFormSettingsKey>
    : never;
export function useFormSettings<
  TFormOrFormExperience extends "MarketoForm" | "FormExperience" = "FormExperience",
  const TFormSettingsKey extends MarketoFormSettingsKey | AnyFormSettingsKey = 
    TFormOrFormExperience extends "MarketoForm"
      ? MarketoFormSettingsKey
  : AnyFormSettingsKey
> (
  { callerLabel }: { callerLabel?: string | undefined } = {}
)
{
  type FormSettingsType = TFormOrFormExperience extends "FormExperience"
    ? IFormSettings & (typeof FormExperienceSettingsObject)[SettingsKeyType<"FormExperience", TFormSettingsKey>]
    : TFormOrFormExperience extends "MarketoForm"
    ? IFormSettings & (typeof MarketoFormSettingsObject)[SettingsKeyType<"MarketoForm", TFormSettingsKey>]
    : never;
      
  const { logger } = useFormLogger();
  try {
    callerLabel ??= "useFormSettings";
    const formSettings = injectRequiredWithSelf<UseFormSettingsInjectionValues["FormSettings"]>(UseFormSettingsInjectionKeys.FormSettings);
    const FormSettings = formSettings as Readonly<FormSettingsType>;
    const FormClasses = injectRequiredWithSelf<UseFormSettingsInjectionValues["FormClasses"]>(UseFormSettingsInjectionKeys.FormClasses);

    return {
      FormSettings,
      FormClasses,
    };
  }
  catch (err) {
    logger.error(`InjectionError from ${callerLabel ?? 'unknown'}`);
    throw err;
  }
}